import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import SelectClinicModal from "../../components/modals/SelectClinicModal";
import Navbar from "../../components/account/Navbar";
import {
    showSuccessToast,
    showErrorToast,
    showInfoToast,
} from "../../utils/notificationUtils";
import { clinicAPI } from "../../services/api";
import "../../styles/clinicsetup.css";
import "../../pages/reception/ReceptionStyles.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const RedirectToClinic = ({ clinicData, clinicId }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [showSelectClinicModal, setShowSelectClinicModal] = useState(false);
    const [ownedClinics, setOwnedClinics] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const checkClinicAndRedirect = async () => {
            try {
                setIsLoading(true);
                const clinicDataResponse = await clinicAPI.getClinicSetupData();
                const data = clinicDataResponse.data?.data || {};

                // Check if user owns clinics
                const ownedClinicsList = data.owned_clinics || [];
                const isOwner = ownedClinicsList.length > 0;

                if (isOwner) {
                    // User owns clinics
                    if (ownedClinicsList.length === 1) {
                        // Only one clinic - auto-select and navigate
                        const singleClinic = ownedClinicsList[0];
                        navigate(`/medical/clinic/${singleClinic.id}/appointment-management`, { replace: true });
                    } else if (ownedClinicsList.length > 1) {
                        // Multiple clinics - check for saved selection first
                        try {
                            // Check dedicated localStorage key first (won't be overwritten by auth)
                            let savedClinicId = localStorage.getItem('selected_clinic_id');

                            // Fallback to user_data if dedicated key doesn't exist
                            if (!savedClinicId) {
                                const userData = localStorage.getItem("user_data");

                                if (userData && userData !== "undefined" && userData !== "null") {
                                    savedClinicId = JSON.parse(userData).clinic_id;
                                }
                            }

                            // Verify the saved clinic is still in the owned clinics list
                            if (savedClinicId) {
                                const savedClinic = ownedClinicsList.find(c => c.id === parseInt(savedClinicId));

                                if (savedClinic) {
                                    // Auto-navigate to saved clinic
                                    navigate(`/medical/clinic/${savedClinicId}/appointment-management`, { replace: true });
                                    return;
                                }
                            }
                        } catch (e) {
                            console.error("Error reading saved clinic:", e);
                        }

                        // No valid saved selection - show modal to select
                        setOwnedClinics(ownedClinicsList);
                        setShowSelectClinicModal(true);
                        setIsLoading(false);
                    } else {
                        // No clinics found, redirect to setup
                        navigate("/medical/clinic/setup", { replace: true });
                    }
                } else {
                    // User is a member (not owner) - check for registered clinic
                    const registeredClinicId = data.registered_clinic_id;

                    if (registeredClinicId) {
                        // Check membership status for current user


                        navigate(`/medical/clinic/${registeredClinicId}/appointment-management`, { replace: true });
                    } else {
                        // No clinic found, redirect to setup
                        navigate("/medical/clinic/setup", { replace: true });
                    }
                }
            } catch (error) {
                console.error("Error checking clinic status:", error);
                showErrorToast("Failed to check clinic status");
                // On error, redirect to setup as fallback
                navigate("/medical/clinic/setup", { replace: true });
            } finally {
                setIsLoading(false);
            }
        };

        checkClinicAndRedirect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    return (
        <>
            {isLoading && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: '20px'
                }}>
                    <div className="loading-spinner"></div>
                    <h1 style={{
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: 500,
                        color: '#64748B',
                        fontFamily: '"Red Hat Display", sans-serif'
                    }}>
                        Redirecting to clinic...
                    </h1>
                </div>
            )}

            <SelectClinicModal
                open={showSelectClinicModal}
                clinics={ownedClinics}
                defaultRoute="appointment-management"
            />
        </>
    );
};

export default RedirectToClinic;
